import Dexie, { IndexableType } from 'dexie';
import {
  SnapshotStores,
  WALMetaData,
  WALStores,
  SnapshotItem,
  SnapshotInfo,
  SnapshotParams,
  walParams,
  SnapshotType,
} from '../../types';
import { encrypt } from '../../../../core/cryptor/cipher';
import { getWALDB, getDecryptedSnapshotInfo, getSnapshotDB } from './utils';
import { prepareSnapshotData } from '../../helpers';
import { saveRecordInUnSyncedDB } from '../../../../core/database/unSynced/unSyncedStorage';
import { Patch } from 'immer';

// Write Operations are done by worker
export class IDBSheetWriter {
  // #region private helpers
  // private async getSnapshotDB(id: string) {
  //   const db = new Dexie(id);
  //   db.version(1).stores(snapshotStoresSchema);
  //   await db.open();

  //   return db;
  // }

  // private async getWALDB(id: string, activeWAL: number) {
  //   const name = getWALDBName(id, activeWAL);
  //   const db = new Dexie(name);
  //   db.version(1).stores(walStoresSchema);

  //   await db.open();
  //   return db;
  // }

  private async getRecordSerial(
    sheetId: string,
    walDB: Dexie,
    feKey: CryptoKey
  ) {
    let serial = undefined;
    const serialsInitiated: boolean = await walDB
      .table(WALStores.MetaData)
      .get(WALMetaData.SerialsInitiated);
    if (serialsInitiated) return serial;

    const count = await walDB.table(WALStores.Changes).count();
    if (!count) {
      const masterDB = await getSnapshotDB(sheetId);
      const snapshotInfo: SnapshotInfo = await getDecryptedSnapshotInfo(
        masterDB,
        feKey
      );

      serial = snapshotInfo.lastSerial + 1;
      masterDB.close();

      await walDB
        .table(WALStores.MetaData)
        .put(true, WALMetaData.SerialsInitiated);
    }

    return serial;
  }

  private async getLastAssignedSerial(sheetId: string, activeWAL: number) {
    let lastAssignedSerial = 0;
    if (activeWAL === 0) return lastAssignedSerial;

    const db = await getWALDB(sheetId, activeWAL);

    const recordKeys = await db.table(WALStores.Changes).toCollection().keys();
    lastAssignedSerial = (recordKeys.pop() as number) ?? 0;

    db.close();

    return lastAssignedSerial;
  }

  private async updateSnapshotInfo(
    db: Dexie,
    feKey: CryptoKey,
    id: string,
    snapshotInfo: SnapshotInfo
  ) {
    const currentActiveWAL = snapshotInfo.activeWAL;
    const newActiveWAL = currentActiveWAL + 1;
    const lastSerial = await this.getLastAssignedSerial(id, currentActiveWAL);
    const newSnapshotInfo = {
      ...snapshotInfo,
      activeWAL: newActiveWAL,
      lastSerial,
    };
    const updatedSnapshotInfo = await encrypt(
      JSON.stringify(newSnapshotInfo),
      'snapshot',
      feKey
    );
    await db
      .table(SnapshotStores.Snapshot)
      .put(updatedSnapshotInfo, SnapshotItem.Info);
  }
  // #endregion

  public removeRecordFromUnSyncWAL() {}

  //   #region snapshot
  public async saveSnapshot(
    feKey: CryptoKey,
    dbName: string,
    snapshotParams: SnapshotParams
  ) {
    const data = prepareSnapshotData(snapshotParams);
    const db = await getSnapshotDB(dbName);

    try {
      const encData = await encrypt(data, 'snapshot', feKey);
      // const t1 = performance.now()
      await db
        .table(SnapshotStores.Snapshot)
        .put(encData, SnapshotItem.Snapshot);
      // const t2 = performance.now()
      // console.log("snapshot save time", t2 - t1 , dbName)

      const snapshotInfo = await getDecryptedSnapshotInfo(db, feKey);

      await this.updateSnapshotInfo(db, feKey, dbName, snapshotInfo);
    } catch (err) {
      db.close();
      // TODO: Post Error Message
      console.log('err', err);
      return;
    }

    db.close();
  }
  //   #endregion

  //   #region save changes
  public async saveChanges(
    feKey: CryptoKey,
    dbName: string,
    walParams: walParams
  ) {
    try {
      let { changes, activeWAL = 0, serverAssignedSerial, walType } = walParams;

      const encChanges = await encrypt(
        JSON.stringify(changes),
        'changes',
        feKey
      );

      let recordKey: string | IndexableType = '';

      // If active WAL is 0 then fetch activeWAL number from master DB
      if (activeWAL === 0) {
        const masterDB = await getSnapshotDB(dbName);
        const snapshotInfo: SnapshotInfo = await getDecryptedSnapshotInfo(
          masterDB,
          feKey
        );

        activeWAL = Math.max(snapshotInfo.activeWAL, 1);
        masterDB.close();
      }
      const db = await getWALDB(dbName, activeWAL);

      const serial: number | undefined = await this.getRecordSerial(
        dbName,
        db,
        feKey
      );

      await db
        .transaction('rw', [WALStores.Changes], async () => {
          recordKey = await db.table(WALStores.Changes).put(encChanges, serial);
        })
        .then(async () => {
          if (serverAssignedSerial) {
            await db
              .table(WALStores.VerifiedSerials)
              .put(serverAssignedSerial, recordKey);
          } else {
            // Save WAL Info in UnSynchedWALs DB
            // There will be component that will check changes in this db and post it to server
            let id = dbName;
            if (walParams.walType === SnapshotType.EntityIdToName) {
              id = walParams.changes[0]?.path[0]?.toString() ?? dbName; // Set id to sheetId as this sheet id will be used by the server to create and send sheetsIdToName with other users
            }
            await saveRecordInUnSyncedDB({
              type: walParams.walType,
              activeWAL,
              localSerial: Number(recordKey),
              id,
            });
          }
        })
        .catch((err) => {
          db.close();
          // TODO: Post Error Message to Main thread
          return;
        });

      db.close();

      // if (walType === SnapshotType.Groups)
      //   await this.saveGroupsIndividually(id, changes, feKey);
    } catch (err) {
      // TODO: Post MEssage
      return;
    }
  }
  //   #endregion
}
